import { loadStripe } from '@stripe/stripe-js';
import { toRgbColor } from '@/utils/color';
import * as styles from './elements.css';

// Publishable keys are not secret. Mirrors the app's other Stripe integration (builder credits):
// prefer an explicit env override, otherwise pick live vs. test by build environment.
const STRIPE_LIVE_PUBLISHABLE_KEY = 'pk_live_VSBgG2Tm1YSfm6SlJ1TImZxU';
const STRIPE_TEST_PUBLISHABLE_KEY = 'pk_test_TxLnsgP18XA8nQB3zQ3abTEH00sI05jHzR';

export const BILLING_STRIPE_PUBLISHABLE_KEY =
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ??
  (__CIO_ENV__ === 'production' ? STRIPE_LIVE_PUBLISHABLE_KEY : STRIPE_TEST_PUBLISHABLE_KEY);

let billingStripePromiseInstance: ReturnType<typeof loadStripe> | undefined;

/**
 * The shared Stripe.js instance for billing card entry, created lazily on first call. Creating it at
 * module scope would inject the Stripe.js `<script>` at app boot for anything that transitively imports
 * this module (e.g. the `@/features/billing` barrel, imported eagerly for `BILLING_PATHS`); the getter
 * defers that fetch to the first card-entry render. `loadStripe` is memoized by the SDK and the promise
 * is cached here, so all billing surfaces (payment methods, checkout) reuse one instance.
 *
 * **Only a successful load is cached.** Caching a rejection would make a transient failure — an ad
 * blocker holding back the script is the common one — permanent for the rest of the session: every later
 * caller gets handed the same rejected promise, so a form that gates submit on the load state (see
 * `useStripeLoad`) keeps asking the user to refresh even once the script would load. `loadStripe` clears
 * its own cache on error for the same reason, so dropping the memo here restores a real retry.
 */
export function getBillingStripePromise(): ReturnType<typeof loadStripe> {
  billingStripePromiseInstance ??= loadStripe(BILLING_STRIPE_PUBLISHABLE_KEY).then(
    (stripe) => {
      // A `null` resolve means there was no DOM to inject the script into — a failed load, not an
      // instance, so it must not be cached either.
      if (stripe == null) {
        billingStripePromiseInstance = undefined;
      }
      return stripe;
    },
    (error: unknown) => {
      billingStripePromiseInstance = undefined;
      throw error;
    },
  );
  return billingStripePromiseInstance;
}

/**
 * In tests we never load the real Stripe.js script or mount its iframes — card entry renders as
 * plain inputs. Consumers pass `null` to `<Elements>` and bypass card-completeness gating in this
 * mode, mirroring the app's builder-credits Stripe flow.
 */
export const IS_STRIPE_TEST_MODE = import.meta.env.MODE === 'test';

// Resolve Pluma theme tokens at call time so the iframe-internal text/placeholder/error colors track
// the active color scheme (light or dark). Pluma authors its tokens in oklch, so they go to Stripe as
// sRGB.
function resolveToken(name: string): string {
  return toRgbColor(getComputedStyle(document.documentElement).getPropertyValue(name).trim());
}

function stripeBaseStyle() {
  return {
    color: resolveToken('--pluma-color-text-primary'),
    // 34px = 36px field height minus the 2×1px border; centers text inside the Stripe iframe.
    lineHeight: '34px',
    '::placeholder': { color: resolveToken('--pluma-color-text-secondary') },
  };
}

function stripeInvalidStyle() {
  return {
    color: resolveToken('--pluma-color-scope-critical-text'),
  };
}

export function stripeCardNumberOptions() {
  return {
    style: { base: stripeBaseStyle(), invalid: stripeInvalidStyle() },
    classes: { base: styles.stripeInput, focus: 'is-focused', invalid: 'is-error' },
    showIcon: true,
  };
}

export function stripeCardDetailOptions() {
  return {
    style: { base: stripeBaseStyle(), invalid: stripeInvalidStyle() },
    classes: { base: styles.stripeInput, focus: 'is-focused', invalid: 'is-error' },
  };
}
